import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CardMedia from '@mui/material/CardMedia'
import CardActionArea from '@mui/material/CardActionArea'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import articles from '../data/articles.json'
import artikelImages from '../assets/artikelImages'

const ArtikelList = () => {
  const navigate = useNavigate()
  const [featured, ...rest] = articles

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', overflow: 'hidden' }}>
      {/* Hero Banner */}
      <Box
        sx={{
          background: [
            'radial-gradient(ellipse at 80% 20%, rgba(37,99,235,0.18) 0%, transparent 60%)',
            'radial-gradient(ellipse at 10% 80%, rgba(249,115,22,0.12) 0%, transparent 55%)',
            '#0f172a',
          ].join(', '),
          pt: { xs: 14, md: 18 },
          pb: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Subtle grid pattern */}
        <Box
          sx={{
            position: 'absolute', inset: 0, opacity: 0.04,
            backgroundImage: 'radial-gradient(rgba(255,255,255,0.8) 1px, transparent 1px)',
            backgroundSize: '32px 32px',
          }}
        />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Stack spacing={2} alignItems="center" textAlign="center">
            <Typography
              sx={{
                fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 3,
                color: '#f97316', fontWeight: 700,
              }}
            >
              Tips & Edukasi Pengasuhan
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: '2.4rem', sm: '3rem', md: '3.6rem' },
                fontWeight: 900, lineHeight: 1.1, color: '#fff',
                maxWidth: 700,
              }}
            >
              Artikel{' '}
              <Box component="span" sx={{ color: '#f97316' }}>Parenting</Box>
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: '1rem', md: '1.1rem' },
                color: 'rgba(255,255,255,0.6)',
                maxWidth: 560, lineHeight: 1.8,
              }}
            >
              Panduan praktis dan edukatif seputar pengasuhan anak, manajemen rumah tangga,
              dan hubungan harmonis antara keluarga dan pengasuh.
            </Typography>
          </Stack>
        </Container>
      </Box>

      {/* Featured Article */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Typography
          sx={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: 2.5, color: '#64748b', textTransform: 'uppercase', mb: 3 }}
        >
          Artikel Terbaru
        </Typography>

        <Card
          elevation={0}
          sx={{
            borderRadius: 4, overflow: 'hidden', border: '1px solid #e2e8f0',
            transition: 'all 0.3s ease',
            '&:hover': { boxShadow: '0 20px 60px rgba(0,0,0,0.1)', transform: 'translateY(-4px)' },
          }}
        >
          <CardActionArea onClick={() => navigate(`/artikel/${featured.slug}`)} sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'stretch' }}>
            <CardMedia
              component="img"
              image={artikelImages[featured.gambar]}
              alt={featured.judul}
              sx={{ width: { xs: '100%', md: '52%' }, height: { xs: 260, md: 420 }, objectFit: 'cover' }}
            />
            <CardContent sx={{ flex: 1, p: { xs: 3, md: 5 }, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Chip
                label={featured.kategori}
                size="small"
                sx={{ mb: 2, alignSelf: 'flex-start', bgcolor: featured.kategoriColor, color: '#fff', fontWeight: 700, fontSize: '0.72rem' }}
              />
              <Typography
                sx={{ fontSize: { xs: '1.5rem', md: '2rem' }, fontWeight: 800, lineHeight: 1.25, color: '#0f172a', mb: 2 }}
              >
                {featured.judul}
              </Typography>
              <Typography sx={{ color: '#64748b', lineHeight: 1.8, fontSize: '0.97rem', mb: 3 }}>
                {featured.ringkasan}
              </Typography>
              <Stack direction="row" spacing={2.5} alignItems="center" flexWrap="wrap" gap={1}>
                <Stack direction="row" spacing={0.7} alignItems="center">
                  <CalendarTodayIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
                  <Typography sx={{ fontSize: '0.8rem', color: '#94a3b8' }}>{featured.tanggal}</Typography>
                </Stack>
                <Stack direction="row" spacing={0.7} alignItems="center">
                  <AccessTimeIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
                  <Typography sx={{ fontSize: '0.8rem', color: '#94a3b8' }}>{featured.readTime}</Typography>
                </Stack>
              </Stack>
              <Button
                endIcon={<ArrowForwardIcon />}
                sx={{
                  mt: 3, alignSelf: 'flex-start', color: '#2563eb', fontWeight: 700,
                  p: 0, fontSize: '0.92rem',
                  '&:hover': { bgcolor: 'transparent', color: '#f97316' },
                }}
              >
                Baca Selengkapnya
              </Button>
            </CardContent>
          </CardActionArea>
        </Card>

        {/* Rest of Articles Grid */}
        <Typography
          sx={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: 2.5, color: '#64748b', textTransform: 'uppercase', mt: 8, mb: 3 }}
        >
          Artikel Lainnya
        </Typography>

        <Grid container spacing={3}>
          {rest.map(article => (
            <Grid item xs={12} sm={6} md={3} key={article.id}>
              <Card
                elevation={0}
                sx={{
                  height: '100%', borderRadius: 3, overflow: 'hidden',
                  border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column',
                  transition: 'all 0.3s ease',
                  '&:hover': { boxShadow: '0 16px 48px rgba(0,0,0,0.1)', transform: 'translateY(-4px)' },
                }}
              >
                <CardActionArea onClick={() => navigate(`/artikel/${article.slug}`)} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
                  <CardMedia
                    component="img"
                    image={artikelImages[article.gambar]}
                    alt={article.judul}
                    sx={{ height: 190, objectFit: 'cover' }}
                  />
                  <CardContent sx={{ flex: 1, p: 2.5, display: 'flex', flexDirection: 'column' }}>
                    <Chip
                      label={article.kategori}
                      size="small"
                      sx={{ mb: 1.5, alignSelf: 'flex-start', bgcolor: article.kategoriColor, color: '#fff', fontWeight: 700, fontSize: '0.68rem' }}
                    />
                    <Typography
                      sx={{ fontSize: '0.95rem', fontWeight: 700, lineHeight: 1.4, color: '#0f172a', mb: 1.5, flex: 1 }}
                    >
                      {article.judul}
                    </Typography>
                    <Typography sx={{ fontSize: '0.82rem', color: '#64748b', lineHeight: 1.65, mb: 2, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {article.ringkasan}
                    </Typography>
                    <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <CalendarTodayIcon sx={{ fontSize: 12, color: '#94a3b8' }} />
                        <Typography sx={{ fontSize: '0.73rem', color: '#94a3b8' }}>{article.tanggal}</Typography>
                      </Stack>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <AccessTimeIcon sx={{ fontSize: 12, color: '#94a3b8' }} />
                        <Typography sx={{ fontSize: '0.73rem', color: '#94a3b8' }}>{article.readTime}</Typography>
                      </Stack>
                    </Stack>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* CTA Section */}
      <Box sx={{ bgcolor: '#0f172a', py: { xs: 8, md: 10 }, mt: 4 }}>
        <Container maxWidth="md">
          <Stack spacing={3} alignItems="center" textAlign="center">
            <Typography sx={{ fontSize: { xs: '1.8rem', md: '2.4rem' }, fontWeight: 800, color: '#fff', lineHeight: 1.2 }}>
              Butuh Pengasuh Profesional<br />
              <Box component="span" sx={{ color: '#f97316' }}>yang Terpercaya?</Box>
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '1rem', maxWidth: 480 }}>
              CV Mulia Utama Mandiri menyediakan tenaga pengasuh terseleksi dan terlatih untuk keluarga Anda.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => window.open('https://wa.me/6281234567890', '_blank')}
              sx={{
                bgcolor: '#f97316', color: '#fff', borderRadius: 99,
                py: 1.6, px: 4, fontWeight: 700, fontSize: '1rem',
                '&:hover': { bgcolor: '#ea6c10' },
              }}
            >
              Hubungi Kami via WhatsApp
            </Button>
          </Stack>
        </Container>
      </Box>
    </Box>
  )
}

export default ArtikelList
